/* eslint-disable react/prop-types */
/** @jsxImportSource @emotion/react */
import React from "react";
import { Form, Input, InputNumber, Switch } from "antd";
import {
  DragEndEvent,
  DragOverEvent,
  DraggableSyntheticListeners,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { arrayMove, useSortable } from "@dnd-kit/sortable";
import { MenuOutlined } from "@ant-design/icons";
import { DragIndexState, InputType } from "./types";
import * as Styles from "./styles";

export const DragIndexContext = React.createContext<DragIndexState>({
  active: -1,
  over: -1,
});

export const dragActiveStyle = (dragState: DragIndexState, id: string) => {
  const { active, over } = dragState;
  let style: React.CSSProperties = {};
  if (active && active === id) {
    style = { backgroundColor: "#E7E7E7", opacity: 0.7 };
  } else if (over && id === over && active !== over) {
    style = { borderInlineStart: "1px dashed gray" };
  }
  return style;
};

interface HeaderCellProps extends React.HTMLAttributes<HTMLTableCellElement> {
  id: string;
}

export const TableHeaderCell: React.FC<HeaderCellProps> = (props) => {
  const dragState = React.useContext(DragIndexContext);
  const { attributes, listeners, setNodeRef, isDragging } = useSortable({
    id: props.id,
  });
  const style: React.CSSProperties = {
    ...props.style,
    cursor: isDragging ? "grabbing" : "grab",
    ...(isDragging
      ? { position: "relative", zIndex: 1, userSelect: "none" }
      : { zIndex: "auto" }),
    ...dragActiveStyle(dragState, props.id),
  };
  return (
    <th
      {...props}
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
    />
  );
};

interface BodyCellProps extends React.HTMLAttributes<HTMLTableCellElement> {
  id: string;
  editing?: boolean;
  inputType?: InputType;
  dataIndex?: string;
  record?: Record<string, unknown>;
}

const editInput = (type?: InputType) => {
  switch (type) {
    case InputType.Text:
      return <Input />;
    case InputType.Number:
      return <InputNumber />;
    case InputType.Switch:
      return <Switch />;
    default:
      return <></>;
  }
};

const valuePropName = (type?: InputType) => {
  switch (type) {
    case InputType.Text:
    case InputType.Number:
      return "value";
    case InputType.Switch:
      return "checked";
    default:
      return "";
  }
};

/** Body cell bound to the table's rowKey so the inline-edit form name is unique. */
export const createTableBodyCell = (
  rowKey: string,
): React.FC<BodyCellProps> => {
  const TableBodyCell: React.FC<BodyCellProps> = (props) => {
    const dragState = React.useContext<DragIndexState>(DragIndexContext);
    const { editing, inputType, dataIndex, record, ...restProps } = props;
    const style = {
      ...props.style,
      ...dragActiveStyle(dragState, props.id),
    };

    if (editing) {
      const formName = `${dataIndex}_${record?.[rowKey]}`;
      return (
        <td {...restProps} style={style}>
          <Form.Item
            name={formName}
            css={Styles.formInputStyle}
            valuePropName={valuePropName(inputType)}
          >
            {editInput(inputType)}
          </Form.Item>
        </td>
      );
    }
    return <td {...restProps} style={style} />;
  };
  return TableBodyCell;
};

/** Column drag/drop reorder over an array of column keys. */
export const useColumnDnd = (
  order: string[],
  setOrder: React.Dispatch<React.SetStateAction<string[]>>,
) => {
  const [dragIndex, setDragIndex] = React.useState<DragIndexState>({
    active: -1,
    over: -1,
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        // https://docs.dndkit.com/api-documentation/sensors/pointer#activation-constraints
        distance: 1,
      },
    }),
  );

  const onDragEnd = React.useCallback(
    ({ active, over }: DragEndEvent) => {
      if (over && active.id !== over.id) {
        setOrder((prev) =>
          arrayMove(
            prev,
            prev.indexOf(active.id as string),
            prev.indexOf(over.id as string),
          ),
        );
      }
      setDragIndex({ active: -1, over: -1 });
    },
    [setOrder],
  );

  const onDragOver = React.useCallback(
    ({ active, over }: DragOverEvent) => {
      const activeIndex = order.indexOf(active.id as string);
      const overIndex = order.indexOf(over?.id as string);
      setDragIndex({
        active: active.id,
        over: over?.id,
        direction: overIndex > activeIndex ? "right" : "left",
      });
    },
    [order],
  );

  return { sensors, dragIndex, onDragEnd, onDragOver };
};

export const RowContext = React.createContext<{
  listeners?: DraggableSyntheticListeners;
}>({});

export const RowDragHandle: React.FC = () => {
  const { listeners } = React.useContext(RowContext);
  return (
    <MenuOutlined
      style={{ cursor: "move", touchAction: "none" }}
      {...listeners}
    />
  );
};

interface SortableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  "data-row-key": string;
}

export const SortableRow: React.FC<SortableRowProps> = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props["data-row-key"] });

  const style: React.CSSProperties = {
    ...props.style,
    transform: transform ? `translate3d(0, ${transform.y}px, 0)` : undefined,
    transition,
    ...(isDragging ? { position: "relative", zIndex: 999 } : {}),
  };

  return (
    <RowContext.Provider value={{ listeners }}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  );
};

export const rowDragColumn = {
  key: "row-drag-handle",
  title: "",
  width: 40,
  // eslint-disable-next-line react/display-name
  render: () => <RowDragHandle />,
};

export const useRowDnd = (
  rowKeys: string[],
  onRowReorder?: (from: number, to: number) => void,
) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 1 },
    }),
  );

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id || !onRowReorder) {
      return;
    }
    const from = rowKeys.indexOf(active.id as string);
    const to = rowKeys.indexOf(over.id as string);
    if (from !== -1 && to !== -1) {
      onRowReorder(from, to);
    }
  };

  return { sensors, onDragEnd };
};
